import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiTransformActions } from 'store';
import { useAiSessionNoteStream } from '../../utils/hooks/useAiSessionNoteStream';
import { sanitizeAiHtml } from '../../utils/sanitizeAiHtml';

/**
 * Fixed instruction for the comment "Improve" button. Two things matter: keep it
 * short/professional, and DON'T let the model touch the `@[staffId]` mention
 * tokens — the backend parses those on save, so mangling one silently drops the
 * mention notification.
 */
const IMPROVE_INSTRUCTION =
    'Rewrite this comment to fix grammar and spelling and make it clear, concise, and professional. ' +
    'Keep it roughly the same length and preserve every @[...] mention token exactly as-is. ' +
    'Return only the rewritten comment text, with no preamble or surrounding quotes.';

/**
 * Concrete `onImprove` wiring for <CommentEditor />, backed by the shared
 * aiTransform store — the same POST-then-stream pipeline the progress-report
 * builder uses (see reportBuilder.js): `transformRichText` returns a
 * conversationId, then chunks arrive over Socket.IO via useAiSessionNoteStream.
 *
 * Returns a handler matching CommentEditor's contract: `onImprove(text,
 * applyImproved)`, where `applyImproved(str)` writes back into the editor. We
 * call it on every chunk, so the comment visibly rewrites itself as the model
 * streams.
 *
 * Single-flight: a second click while an improve is in progress is ignored.
 * Because it binds a socket listener for its lifetime, mount it only where AI
 * improve is actually wanted (e.g. via <CommentEditorAi /> under
 * <Comments enableAiImprove />), not on every comment editor in the app.
 */
export const useCommentImprove = () => {
    const dispatch = useDispatch();
    const conversationId = useSelector((state) => state.aiTransform?.transform?.conversationId || null);

    // The editor's write-back callback for the in-flight improve, captured on click.
    const applyRef = useRef(null);
    // True only between a click and the stream settling — so the conversationId
    // effect ignores ids left over from anything else that touched aiTransform.
    const pendingRef = useRef(false);
    const lastConvIdRef = useRef(null);

    const onChunk = useCallback(({ content }) => {
        applyRef.current?.(sanitizeAiHtml(content));
    }, []);
    const { start, stop, isDone, isError } = useAiSessionNoteStream({ onUpdate: onChunk });

    // Saga issued a conversationId for our request → begin streaming its chunks.
    useEffect(() => {
        if (!pendingRef.current || !conversationId) return;
        if (lastConvIdRef.current === conversationId) return;
        lastConvIdRef.current = conversationId;
        start(conversationId);
    }, [conversationId, start]);

    // Stream settled → release the editor and reset the shared slice, so the
    // next improve (here or elsewhere) starts from a clean conversationId.
    useEffect(() => {
        if (!pendingRef.current || (!isDone && !isError)) return;
        pendingRef.current = false;
        applyRef.current = null;
        dispatch(aiTransformActions.resetAiTransform());
    }, [isDone, isError, dispatch]);

    // Abandon any in-flight stream on unmount (e.g. popover closed mid-generation).
    useEffect(
        () => () => {
            stop();
            dispatch(aiTransformActions.resetAiTransform());
        },
        [stop, dispatch],
    );

    return useCallback(
        (text, applyImproved) => {
            if (!text || pendingRef.current) return;
            applyRef.current = applyImproved;
            pendingRef.current = true;
            lastConvIdRef.current = null;
            dispatch(aiTransformActions.transformRichText(text, IMPROVE_INSTRUCTION));
        },
        [dispatch],
    );
};
